import {
  KatDropdown, KatOption, KatIcon, KatModal, KatRadiobuttonGroup, KatButton, KatAlert,
} from '@amzn/katal-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMetrics, useTranslation } from 'src/utils';
import { ButtonTypes } from 'src/constants/metrics';
import FetchStatus from 'src/types/FetchStatus';

export interface DropdownWithDefaultProps<T> {
  value?: T,
  onChange: (updatedValue: T) => void,
  options: { name: string, value: T, disableDefault?: boolean, constraintLabel?: string }[],
  // save preference modal title
  modalTitle: string,
  modalContent: string,
  defaultSelectedOption: string,
  onSaveDefault: (value: T) => void,
  dataTestId?: string,
  allowDefault?: boolean
  modalFailureMessage: string
  modalFetchStatus: FetchStatus
}
const DropdownWithDefault = <T extends string> ({
  value,
  onChange,
  options,
  modalTitle,
  modalContent,
  dataTestId,
  allowDefault,
  defaultSelectedOption,
  onSaveDefault,
  modalFailureMessage,
  modalFetchStatus,
}: DropdownWithDefaultProps<T>) => {
  const { t } = useTranslation();
  const { clickMetric } = useMetrics();
  const [displayModal, setDisplayModal] = useState(false);
  const [preferenceOption, setPreferenceOption] = useState<T | undefined>();

  useEffect(() => {
    if (modalFetchStatus === FetchStatus.complete) {
      setDisplayModal(false);
    }
  }, [modalFetchStatus]);
  const generateDropdownOptions = () => {
    const defaultOption = allowDefault && defaultSelectedOption;
    const dropdownOptions = options.map((
      {
        value: optionValue, name,
      },
    ) => (
      <KatOption
        data-testid={`dropdown-option-${optionValue}`}
        key={`${optionValue}-${defaultOption === optionValue}`}
        value={optionValue}
      >
        {
        defaultOption === optionValue ? t('preferenceDropdown_default_option', { optionValue: name }) : name
        }
      </KatOption>
    ));
    const changeDefaultOption = (
      <KatOption className="border border-solid border-kat-athens border-x-transparent bg-kat-snow" data-testid={`change-default-${dataTestId}`} key="change-default" onClick={() => setDisplayModal(true)}>
        <span className="flex content-center justify-between ">
          {t('preferenceDropdown_change_default')}
          <KatIcon size="small" name="playlist_add_check" />
        </span>
      </KatOption>
    );
    if (allowDefault) {
      dropdownOptions.push(changeDefaultOption);
    }

    return dropdownOptions;
  };

  const onSaveHandler = () => {
    if (preferenceOption) {
      onSaveDefault(preferenceOption);
      clickMetric({
        metricValue: preferenceOption,
        metricName: ButtonTypes.saveDefaultPreference,
      });
      onChange(preferenceOption);
    }
  };

  const onChangeHandler = (e: KatDropdown.ChangeEvent) => onChange(e.detail.value as T);

  return (
    <>
      <KatDropdown
        data-testid={dataTestId}
        value={value}
        onChange={onChangeHandler}
      >
        {generateDropdownOptions()}
      </KatDropdown>
      {
      displayModal && createPortal(
        <KatModal
          data-testid="change-default-modal"
          title={modalTitle}
          visible={displayModal}
          onClose={() => setDisplayModal(false)}
        >
          <KatRadiobuttonGroup
            onChange={(e) => setPreferenceOption(e.detail.value as T)}
            label={modalContent}
            value={preferenceOption}
            name="change-default-radio-group"
            options={options.map(
              ({
                name, value: optionValue, disableDefault, constraintLabel,
              }) => ({
                value: optionValue,
                label: name,
                disabled: disableDefault,
                constraintLabel,
              }),
            )}
          />
          {
            modalFetchStatus === FetchStatus.failed && (
              <KatAlert
                data-testid="default-dropdown-error-message"
                variant="warning"
                header={modalFailureMessage}
                description={t('reload_page')}
                className="container mt-5"
                persistent
              />
            )
          }

          <div slot="footer">
            <KatButton
              disabled={!preferenceOption}
              variant="primary"
              onClick={onSaveHandler}
              data-testid="save-preference"
              loading={modalFetchStatus === FetchStatus.loading}
            >
              {t('save')}
            </KatButton>
          </div>
        </KatModal>,
        document.querySelector('#seller-forums') as Element,
      )
    }
    </>
  );
};

DropdownWithDefault.defaultProps = {
  value: undefined,
  dataTestId: undefined,
  allowDefault: true,
};
export default DropdownWithDefault;
